import React from 'react'
import { Tooltip } from 'antd'
import { Rnd } from 'react-rnd'
import { T, speakerColor, speakerName } from './SharedLayout'
import TimelineMarkers from './TimelineMarkers'

/**
 * TimelineTrack
 *
 * The proportional segment bar with playhead and shot markers.
 */
const TimelineTrack = ({
  segments,
  shots,
  totalMs,
  playheadMs,
  isPlaying,
  characterMap,
  onSegmentClick,
  onPauseResize
}) => {
  const [trackWidth, setTrackWidth] = React.useState(0)
  const trackRef = React.useRef(null)
  const [resizingIdx, setResizingIdx] = React.useState(null)
  const [resizePreviewMs, setResizePreviewMs] = React.useState(null)

  React.useEffect(() => {
    if (!trackRef.current) return
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setTrackWidth(entry.contentRect.width)
      }
    })
    resizeObserver.observe(trackRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  if (!totalMs) return null
  const playheadPct = (playheadMs / totalMs) * 100
  const msToPx = trackWidth / totalMs

  return (
    <div
      ref={trackRef}
      style={{
        position: 'relative',
        height: 32,
        borderRadius: 8,
        background: T.surface,
        border: `1px solid ${T.border}`,
        overflow: 'hidden', // Still want to clip playhead if needed, but Rnd might need visible overflow for handles? No, ew-resize is fine.
        marginBottom: 12
      }}
    >
      {/* Segments Mapping */}
      {segments.map((seg, i) => {
        const isPause = seg.type === 'pause'
        const isResizing = resizingIdx === i
        const displayDuration = isResizing
          ? resizePreviewMs
          : seg.end_ms - seg.start_ms

        const leftPx = (seg.start_ms / totalMs) * trackWidth
        const widthPx = Math.max(4, (displayDuration / totalMs) * trackWidth)
        const color = isPause ? T.borderLight : speakerColor(seg.speaker)

        if (isPause) {
          return (
            <Tooltip
              key={i}
              mouseEnterDelay={0.15}
              title={`Pause · ${Math.round(displayDuration)}ms${
                isResizing ? ' (Dragging...)' : ''
              }`}
            >
              <Rnd
                size={{ width: widthPx, height: 12 }}
                position={{ x: leftPx, y: 10 }}
                disableDragging
                enableResizing={{
                  top: false,
                  right: true,
                  bottom: false,
                  left: false,
                  topRight: false,
                  bottomRight: false,
                  bottomLeft: false,
                  topLeft: false
                }}
                onResizeStart={() => {
                  setResizingIdx(i)
                  setResizePreviewMs(seg.duration_ms)
                }}
                onResize={(e, direction, ref, delta, position) => {
                  const newWidthPx = ref.offsetWidth
                  const newMs = Math.round(newWidthPx / msToPx)
                  setResizePreviewMs(Math.max(10, newMs))
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  const newWidthPx = ref.offsetWidth
                  const newMs = Math.round(newWidthPx / msToPx)
                  setResizingIdx(null)
                  setResizePreviewMs(null)
                  if (newMs !== seg.duration_ms) {
                    onPauseResize && onPauseResize(i, newMs)
                  }
                }}
                style={{
                  background: isResizing ? T.blueBorder : color,
                  opacity: isResizing ? 0.9 : 0.65,
                  borderRadius: 4,
                  borderRight: `3px solid ${T.inkLight}`,
                  boxSizing: 'border-box',
                  zIndex: isResizing ? 20 : 4,
                  boxShadow: isResizing ? `0 0 8px ${T.blue}40` : 'none',
                  transition: isResizing
                    ? 'none'
                    : 'background 0.2s, opacity 0.2s'
                }}
              />
            </Tooltip>
          )
        }

        return (
          <Tooltip
            key={i}
            mouseEnterDelay={0.15}
            title={`${speakerName(seg.speaker, characterMap)}: "${(
              seg.text || ''
            ).slice(0, 50)}…"`}
          >
            <div
              onClick={() => onSegmentClick && onSegmentClick(i)}
              style={{
                position: 'absolute',
                left: `${(seg.start_ms / totalMs) * 100}%`,
                width: `${((seg.end_ms - seg.start_ms) / totalMs) * 100}%`,
                top: 4,
                height: 24,
                background: color,
                opacity: 0.65,
                borderRadius: 4,
                borderRight: '1px solid rgba(255,255,255,0.4)',
                boxSizing: 'border-box',
                minWidth: 4,
                cursor: 'pointer',
                transition: 'all 0.12s ease-out',
                zIndex: 4
              }}
            />
          </Tooltip>
        )
      })}

      {/* Shot Markers */}
      <TimelineMarkers shots={shots} totalMs={totalMs} />

      {/* Playhead */}
      {isPlaying && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${playheadPct}%`,
            width: 2,
            background: T.red,
            borderRadius: 1,
            zIndex: 10,
            pointerEvents: 'none',
            boxShadow: '0 0 4px rgba(220, 38, 38, 0.4)'
          }}
        />
      )}
    </div>
  )
}

export default TimelineTrack
